import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import UserAvatar from '../components/UserAvatar';
import UserAssignPicker from '../components/UserAssignPicker';
import { fetchCompanyUsers } from '../utils/companyUsers';
import { canAccessFeature, isCompanyAdmin, isCompanyEmployee, canParticipateInAssignedTasks, canClaimOrReturnHandout } from '../utils/planAccess';
import UpgradeCta from '../components/UpgradeCta';
import BannerImageField from '../components/BannerImageField';
import { getUploadUrl } from '../utils/uploadUrl';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import Lightbox from '../components/Lightbox';
import TaskDetailHeader from '../components/TaskDetailHeader';
import TaskChecklist from '../components/TaskChecklist';
import TaskPhotos, { checkInHasPhotos } from '../components/TaskPhotos';
import {
  TaskNotesCard,
  TaskTimeSummaryCard,
  TaskActionsCard,
  TaskCompletedCard,
  TaskSessionEndModal
} from '../components/TaskCheckInOut';
import TaskCheckInHistory from '../components/TaskCheckInHistory';
import { useConfirm } from '../context/ConfirmContext';
import { formatTaskPriority } from '../utils/taskLabels';
import { formatDateTime } from '../utils/locale';
import {
  buildSessionTimeFields,
  cumulativeWorkedMinutes,
  fromDateTimeLocalValue,
  parsePauseLunchMinutes,
  sessionNetMinutes
} from '../utils/taskCheckInTime';
import TaskFortnoxBillableSection from '../components/TaskFortnoxBillableSection';
import TaskBillingStatusSection from '../components/TaskBillingStatusSection';
import TaskArticlesSection from '../components/TaskArticlesSection';
import TaskArticleFields from '../components/TaskArticleFields';
import WorkGroupSelect from '../components/WorkGroupSelect';
import { buildArticleQuantitiesPayload } from '../utils/articleQuantitiesPayload';
import { canClaimTask } from '../utils/taskClaim';
import { balanceTaskDetailColumns } from '../utils/balanceTaskDetailColumns';
import { isTaskRemoved } from '../utils/taskFilters';
import RecurrenceWeekdayPicker from '../components/RecurrenceWeekdayPicker';
import { normalizeWeekdays } from '../utils/recurrenceWeekdays';
import './TaskDetail.css';

const TaskDetail = () => {
  const { t } = useTranslation(['tasks', 'common', 'articles']);
  const confirm = useConfirm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [presetChecklists, setPresetChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [articles, setArticles] = useState([]);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    estimatedDuration: '',
    location: '',
    locationId: '',
    customerId: '',
    articleId: '',
    articleQuantity: '',
    workGroupId: '',
    workGroupName: '',
    checklist: [],
    scheduledFor: '',
    recurrenceEnabled: false,
    recurrenceIntervalDays: '',
    recurrenceWeekdays: []
  });
  const [workGroups, setWorkGroups] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [checkOutData, setCheckOutData] = useState({
    summary: '',
    beforePhotos: [],
    afterPhotos: []
  });
  const [taskArticleLines, setTaskArticleLines] = useState([]);
  const [doneArticleQuantities, setDoneArticleQuantities] = useState({});
  const [sessionModalMode, setSessionModalMode] = useState(null);
  const [sessionSubmitting, setSessionSubmitting] = useState(false);
  const [markDoneSubmitting, setMarkDoneSubmitting] = useState(false);
  const [markDoneArticlesModalOpen, setMarkDoneArticlesModalOpen] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    took_lunch: false,
    lunch_minutes: '30',
    timeSpent: '',
    endTimeLocal: '',
    manual: false
  });
  const [editBannerFile, setEditBannerFile] = useState(null);
  const [removeTaskBanner, setRemoveTaskBanner] = useState(false);
  const [publishAssignees, setPublishAssignees] = useState([]);
  const editBannerInputRef = useRef(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [locationHandouts, setLocationHandouts] = useState([]);
  const [handoutActionId, setHandoutActionId] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [permanentDeleteSubmitting, setPermanentDeleteSubmitting] = useState(false);

  const fetchTask = useCallback(async () => {
    try {
      const response = await axios.get(`/api/tasks/${id}`);
      const taskData = response.data.data;
      setTask(taskData);

      // Set edit form data
      // Ensure checklist is always an array
      let checklist = [];
      if (taskData.checklist) {
        if (Array.isArray(taskData.checklist)) {
          checklist = taskData.checklist;
        } else if (typeof taskData.checklist === 'string') {
          try {
            const parsed = JSON.parse(taskData.checklist);
            checklist = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            checklist = [];
          }
        }
      }

      setEditFormData({
        title: taskData.title || '',
        description: taskData.description || '',
        priority: taskData.priority || 'medium',
        estimatedDuration: taskData.estimated_duration || '',
        location: taskData.location || '',
        locationId: taskData.location_id || '',
        customerId: taskData.customer_id || '',
        articleId: taskData.article_id || '',
        articleQuantity: taskData.article_quantity != null ? String(taskData.article_quantity) : '',
        workGroupId: taskData.work_group_id || '',
        workGroupName: '',
        checklist: checklist,
        scheduledFor: taskData.scheduled_for ? taskData.scheduled_for.split('T')[0] : '',
        recurrenceEnabled: taskData.recurrence_enabled || false,
        recurrenceIntervalDays: taskData.recurrence_interval_days || '',
        recurrenceWeekdays: normalizeWeekdays(taskData.recurrence_weekdays)
      });

      // Check if user has active check-in
      const activeCheckIn = taskData.check_ins?.find(
        (ci) => ci.user_id === user.id && !ci.check_out_time
      );

      if (activeCheckIn) {
        setCheckInTime(activeCheckIn.check_in_time);
        setCheckOutData((prev) => ({
          ...prev,
          summary: prev.summary || '',
          beforePhotos: prev.beforePhotos || [],
          afterPhotos: prev.afterPhotos || []
        }));
      } else {
        setCheckInTime(null);
        setCheckOutData({
          summary: '',
          beforePhotos: [],
          afterPhotos: []
        });
      }
      setEditBannerFile(null);
      setRemoveTaskBanner(false);
    } catch (error) {
      console.error('Error fetching task:', error);
      setSuccess('');
      setError(t('tasks:failedLoadTask'));
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  }, [id, user, t]);

  const refreshTaskPreservingScroll = useCallback(async () => {
    const scrollY = window.scrollY;
    const ok = await fetchTask();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollY) > 2) {
          window.scrollTo(0, scrollY);
        }
      });
    });
    return ok;
  }, [fetchTask]);

  const formatTime = (minutes) => {
    if (!minutes) return t('tasks:durationZero');
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return t('tasks:durationHoursMinutes', {
        hours,
        hoursPlural: hours > 1 ? 's' : '',
        minutes: mins,
        minutesPlural: mins !== 1 ? 's' : ''
      });
    }
    return t('tasks:durationMinutesOnly', {
      minutes: mins,
      minutesPlural: mins !== 1 ? 's' : ''
    });
  };

  useEffect(() => {
    fetchTask();
    if (user?.role === 'super_admin' || isCompanyAdmin(user)) {
      fetchLocations();
      fetchCustomers();
      fetchArticles();
      if (canAccessFeature(user, 'checklistPresets')) {
        fetchPresetChecklists();
      }
      if (canAccessFeature(user, 'taskWorkGroups')) {
        fetchWorkGroups();
      } else {
        setWorkGroups([]);
      }
    }
  }, [fetchTask, user?.role, user?.active_company_id, user?.active_company_role]);

  useEffect(() => {
    setTaskArticleLines([]);
    setDoneArticleQuantities({});
  }, [id]);

  useEffect(() => {
    if (!sessionModalMode || !checkInTime || sessionForm.manual) {
      return undefined;
    }
    const tick = () => {
      const sessionFields = buildSessionTimeFields(checkInTime);
      setSessionForm((form) => {
        if (form.manual) {
          return form;
        }
        const nextTimeSpent = sessionFields.timeSpent || form.timeSpent;
        if (
          form.timeSpent === nextTimeSpent &&
          form.endTimeLocal === sessionFields.endTimeLocal
        ) {
          return form;
        }
        return {
          ...form,
          timeSpent: nextTimeSpent,
          endTimeLocal: sessionFields.endTimeLocal
        };
      });
    };
    tick();
    const intervalId = window.setInterval(tick, 10000);
    return () => window.clearInterval(intervalId);
  }, [sessionModalMode, checkInTime, sessionForm.manual]);

  useEffect(() => {
    const loadCompanyUsers = async () => {
      if (!task?.company_id || !isCompanyAdmin(user)) {
        return;
      }

      try {
        const companyUsers = await fetchCompanyUsers(task.company_id, user);
        setUsers(companyUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    loadCompanyUsers();
  }, [task?.company_id, user]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/customers');
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    }
  };

  const fetchWorkGroups = async () => {
    try {
      const response = await axios.get('/api/work-groups');
      setWorkGroups(response.data.data || []);
    } catch (error) {
      setWorkGroups([]);
      if (error.response?.status !== 403) {
        console.error('Error fetching work groups:', error);
      }
    }
  };

  const fetchArticles = async () => {
    try {
      const companyId = user?.active_company_id || user?.company_id;
      const response = await axios.get('/api/articles', {
        params: companyId ? { companyId } : undefined
      });
      setArticles(response.data.data || []);
    } catch (error) {
      setArticles([]);
      if (error.response?.status !== 403) {
        console.error('Error fetching articles:', error);
      }
    }
  };

  const fetchPresetChecklists = async () => {
    try {
      const response = await axios.get('/api/preset-checklists');
      setPresetChecklists(response.data.data || []);
    } catch (error) {
      setPresetChecklists([]);
      if (error.response?.status !== 403) {
        console.error('Error fetching preset checklists:', error);
      }
    }
  };

  const fetchLocationHandouts = useCallback(async (locationId) => {
    if (!locationId) {
      setLocationHandouts([]);
      return;
    }

    try {
      const response = await axios.get(`/api/handouts/by-location/${locationId}`);
      setLocationHandouts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching location handouts:', error);
      setLocationHandouts([]);
    }
  }, []);

  useEffect(() => {
    if (task?.location_id) {
      fetchLocationHandouts(task.location_id);
    } else {
      setLocationHandouts([]);
    }
  }, [task?.location_id, fetchLocationHandouts]);

  const getHandoutHolderLabel = (handout) => {
    const name = `${handout.assignee_first_name || ''} ${handout.assignee_last_name || ''}`.trim();
    return name || handout.assignee_email || t('tasks:unknownSubmitter');
  };

  const handleClaimHandout = async (handoutId) => {
    if (handoutActionId) {
      return;
    }
    setHandoutActionId(handoutId);
    try {
      await axios.post(`/api/handouts/${handoutId}/claim`);
      if (task?.location_id) {
        await fetchLocationHandouts(task.location_id);
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedClaimHandout'));
    } finally {
      setHandoutActionId(null);
    }
  };

  const handleClaimTask = async () => {
    if (claiming) {
      return;
    }
    setClaiming(true);
    setError('');
    try {
      await axios.post(`/api/tasks/${id}/claim`);
      await fetchTask();
    } catch (error) {
      setError(formatError(error) || t('tasks:failedClaimTask'));
    } finally {
      setClaiming(false);
    }
  };

  const handleReturnHandout = async (handoutId) => {
    if (handoutActionId) {
      return;
    }
    setHandoutActionId(handoutId);
    try {
      await axios.post(`/api/handouts/${handoutId}/return`);
      if (task?.location_id) {
        await fetchLocationHandouts(task.location_id);
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedReturnHandout'));
    } finally {
      setHandoutActionId(null);
    }
  };

  const renderHandoutStatus = (handout) => {
    if (!handout.assigned_to) {
      if (canClaimOrReturnHandout(user)) {
        return (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={handoutActionId === handout.id}
            onClick={() => handleClaimHandout(handout.id)}
          >
            {t('tasks:handoutClaim')}
          </button>
        );
      }
      return <span className="task-handout-status">{t('tasks:handoutUnclaimed')}</span>;
    }

    if (handout.assigned_to === user.id) {
      if (canClaimOrReturnHandout(user)) {
        return (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={handoutActionId === handout.id}
            onClick={() => handleReturnHandout(handout.id)}
          >
            {t('tasks:handoutReturn')}
          </button>
        );
      }
      return <span className="task-handout-status">{t('tasks:handoutHeldByYou')}</span>;
    }

    return (
      <span className="task-handout-status">
        {t('tasks:handoutHeldBy', { name: getHandoutHolderLabel(handout) })}
      </span>
    );
  };

  const handlePresetChecklistChange = (e) => {
    const presetId = e.target.value;
    if (presetId) {
      const preset = presetChecklists.find(p => p.id === parseInt(presetId));
      if (preset && preset.items) {
        setEditFormData(prev => ({
          ...prev,
          checklist: Array.isArray(preset.items) ? [...preset.items] : []
        }));
      }
    } else {
      setEditFormData(prev => ({
        ...prev,
        checklist: []
      }));
    }
  };

  // Find matching checklist preset by comparing checklist items
  const findMatchingChecklistPreset = () => {
    if (!task || !task.checklist || !Array.isArray(task.checklist) || task.checklist.length === 0) {
      return null;
    }
    if (!presetChecklists || presetChecklists.length === 0) {
      return null;
    }

    // Normalize task checklist items to strings for comparison
    const taskItems = task.checklist.map(item => {
      if (typeof item === 'string') {
        return item.trim().toLowerCase();
      }
      if (item && typeof item === 'object' && item !== null) {
        return (item.text || '').trim().toLowerCase();
      }
      return String(item || '').trim().toLowerCase();
    }).filter(item => item !== '');

    // Find preset that matches
    for (const preset of presetChecklists) {
      if (!preset.items || !Array.isArray(preset.items)) {
        continue;
      }

      // Normalize preset items to strings for comparison
      const presetItems = preset.items.map(item => {
        if (typeof item === 'string') {
          return item.trim().toLowerCase();
        }
        if (item && typeof item === 'object' && item !== null) {
          return (item.text || item || '').trim().toLowerCase();
        }
        return String(item || '').trim().toLowerCase();
      }).filter(item => item !== '');

      // Check if all preset items are in task items and counts match
      if (presetItems.length === taskItems.length && presetItems.length > 0) {
        const taskItemsSet = new Set(taskItems);
        const allMatch = presetItems.every(presetItem => taskItemsSet.has(presetItem));
        if (allMatch) {
          return preset;
        }
      }
    }

    return null;
  };

  const handleEdit = () => {
    setEditing(true);
    setEditBannerFile(null);
    setRemoveTaskBanner(false);
    setPublishAssignees([]);
  };

  const handleEditCancel = () => {
    setEditing(false);
    setEditBannerFile(null);
    setRemoveTaskBanner(false);
    setPublishAssignees([]);
    setError('');
    if (task) {
      // Ensure checklist is always an array
      let checklist = [];
      if (task.checklist) {
        if (Array.isArray(task.checklist)) {
          checklist = task.checklist;
        } else if (typeof task.checklist === 'string') {
          try {
            const parsed = JSON.parse(task.checklist);
            checklist = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            checklist = [];
          }
        }
      }

      setEditFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        estimatedDuration: task.estimated_duration || '',
        location: task.location || '',
        locationId: task.location_id || '',
        customerId: task.customer_id || '',
        articleId: task.article_id || '',
        articleQuantity: task.article_quantity != null ? String(task.article_quantity) : '',
        workGroupId: task.work_group_id || '',
        workGroupName: '',
        checklist: checklist,
        scheduledFor: task.scheduled_for ? task.scheduled_for.split('T')[0] : '',
        recurrenceEnabled: task.recurrence_enabled || false,
        recurrenceIntervalDays: task.recurrence_interval_days || '',
        recurrenceWeekdays: normalizeWeekdays(task.recurrence_weekdays)
      });
    }
  };

  const handleArticleFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value
      };
      if (name === 'articleId') {
        const selected = articles.find((article) => String(article.id) === String(value));
        if (!selected || selected.unit_mode === 'time') {
          updated.articleQuantity = '';
        }
      }
      return updated;
    });
  };

  const handleTaskArticleLinesChange = useCallback((lines) => {
    setTaskArticleLines(lines || []);
    setDoneArticleQuantities((prev) => {
      const next = { ...prev };
      (lines || []).forEach((line) => {
        if (
          (line.unitMode === 'quantity' || line.unitMode === 'both')
          && next[line.id] === undefined
          && line.quantity != null
        ) {
          next[line.id] = String(Math.trunc(Number(line.quantity)));
        }
      });
      return next;
    });
  }, []);

  const ensureTaskArticleLines = async () => {
    if (taskArticleLines.length > 0) {
      return taskArticleLines;
    }
    try {
      const response = await axios.get(`/api/tasks/${id}/articles`);
      const lines = response.data?.data?.lines || [];
      handleTaskArticleLinesChange(lines);
      return lines;
    } catch (err) {
      return taskArticleLines;
    }
  };

  const buildEditPayload = (options = {}) => {
    const payload = new FormData();
    payload.append('title', editFormData.title);
    payload.append('description', editFormData.description || '');

    if (!options.draftEmployeeEdit) {
      payload.append('priority', editFormData.priority || 'medium');
      payload.append('estimatedDuration', editFormData.estimatedDuration || '');
      const checklistForSubmit = (editFormData.checklist || []).map((item) => {
        if (typeof item === 'string') {
          return { text: item, checked: false };
        }
        return { text: item.text || item, checked: item.checked || false };
      });
      payload.append('checklist', JSON.stringify(checklistForSubmit));
      payload.append('recurrenceEnabled', editFormData.recurrenceEnabled ? 'true' : 'false');
      payload.append(
        'recurrenceWeekdays',
        JSON.stringify(editFormData.recurrenceEnabled ? (editFormData.recurrenceWeekdays || []) : [])
      );
      payload.append(
        'recurrenceIntervalDays',
        editFormData.recurrenceEnabled && editFormData.recurrenceWeekdays?.length
          ? ''
          : (editFormData.recurrenceIntervalDays || '')
      );
      if (editFormData.scheduledFor) {
        payload.append('scheduledFor', editFormData.scheduledFor);
      } else {
        payload.append('scheduledFor', '');
      }
      if (editFormData.locationId) {
        payload.append('locationId', editFormData.locationId);
      } else {
        payload.append('locationId', '');
      }
      if (editFormData.customerId) {
        payload.append('customerId', editFormData.customerId);
      } else {
        payload.append('customerId', '');
      }
      if (isCompanyAdmin(user) && canAccessFeature(user, 'taskWorkGroups')) {
        if (editFormData.workGroupName) {
          payload.append('workGroupName', editFormData.workGroupName);
        } else if (editFormData.workGroupId) {
          payload.append('workGroupId', editFormData.workGroupId);
        } else {
          payload.append('workGroupId', '');
        }
      }
      payload.append('articleId', editFormData.articleId || '');
      payload.append(
        'articleQuantity',
        editFormData.articleQuantity !== '' && editFormData.articleQuantity != null
          ? editFormData.articleQuantity
          : ''
      );
    }

    if (editBannerFile) {
      payload.append('banner', editBannerFile);
    }
    if (removeTaskBanner) {
      payload.append('removeBanner', 'true');
    }

    if (options.publish) {
      payload.append('publish', 'true');
      if (publishAssignees.length > 0) {
        payload.append('assignUserIds', JSON.stringify(publishAssignees.map((assignee) => assignee.id)));
      }
    }

    return payload;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      editFormData.recurrenceEnabled
      && (!editFormData.recurrenceWeekdays || editFormData.recurrenceWeekdays.length === 0)
      && !editFormData.recurrenceIntervalDays
    ) {
      setError(t('tasks:recurrenceWeekdaysRequired'));
      return;
    }

    try {
      const payload = buildEditPayload({
        draftEmployeeEdit:
          task?.status === 'draft' &&
          task?.created_by === user?.id &&
          isCompanyEmployee(user)
      });
      await axios.put(`/api/tasks/${id}`, payload);
      setEditing(false);
      setEditBannerFile(null);
      setRemoveTaskBanner(false);
      setPublishAssignees([]);
      fetchTask();
      if (canAccessFeature(user, 'taskWorkGroups')) {
        fetchWorkGroups();
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedUpdateTask'));
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    setError('');

    if (
      editFormData.recurrenceEnabled
      && (!editFormData.recurrenceWeekdays || editFormData.recurrenceWeekdays.length === 0)
      && !editFormData.recurrenceIntervalDays
    ) {
      setError(t('tasks:recurrenceWeekdaysRequired'));
      return;
    }

    try {
      const payload = buildEditPayload({ publish: true });
      await axios.put(`/api/tasks/${id}`, payload);
      setEditing(false);
      setEditBannerFile(null);
      setRemoveTaskBanner(false);
      setPublishAssignees([]);
      navigate('/tasks');
    } catch (error) {
      setError(formatError(error) || t('tasks:failedPublishTask'));
    }
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      const currentChecklist = Array.isArray(editFormData.checklist) ? editFormData.checklist : [];
      setEditFormData({
        ...editFormData,
        checklist: [...currentChecklist, newChecklistItem.trim()]
      });
      setNewChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (index) => {
    const currentChecklist = Array.isArray(editFormData.checklist) ? editFormData.checklist : [];
    setEditFormData({
      ...editFormData,
      checklist: currentChecklist.filter((_, i) => i !== index)
    });
  };

  const handleAssignUser = async (selectedUser) => {
    setError('');

    try {
      await axios.post(`/api/tasks/${id}/assign`, { userId: selectedUser.id });
      fetchTask();
    } catch (error) {
      setError(formatError(error) || t('tasks:failedAssignUser'));
    }
  };

  const handleUnassign = async (assignmentId) => {
    if (!(await confirm(t('tasks:confirmUnassign'), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/tasks/${id}/assign/${assignmentId}`);
      fetchTask();
    } catch (error) {
      setError(formatError(error) || t('tasks:failedUnassignUser'));
    }
  };

  const renderIdleSessionCard = (titleKey, hintKey) => (
    <div className="task-detail-section card task-session-idle">
      <h3 className="task-section-label">{t(titleKey)}</h3>
      <p className="task-session-idle-hint">{t(hintKey)}</p>
    </div>
  );

  const handleCheckIn = async () => {
    setError('');
    setSuccess('');
    try {
      await axios.post(`/api/tasks/${id}/checkin`);
      setCheckInTime(new Date().toISOString());
      const refreshed = await refreshTaskPreservingScroll();
      if (refreshed) {
        setSuccess(t('tasks:checkInSuccess'));
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedCheckIn'));
    }
  };

  const openSessionModal = (mode) => {
    const sessionFields = buildSessionTimeFields(checkInTime);
    setSessionForm({
      took_lunch: false,
      lunch_minutes: '30',
      timeSpent: sessionFields.timeSpent,
      endTimeLocal: sessionFields.endTimeLocal,
      manual: false
    });
    setSessionModalMode(mode);
    if (mode === 'done') {
      ensureTaskArticleLines();
    }
  };

  const handlePauseCheckout = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    try {
      setSessionSubmitting(true);
      const lunchMinutes = parsePauseLunchMinutes(sessionForm.took_lunch, sessionForm.lunch_minutes);
      const payload = {
        lunch_minutes: lunchMinutes
      };
      // Omit corrections when unchanged so sub-minute "now" checkout still works.
      if (sessionForm.manual) {
        const checkOutIso = fromDateTimeLocalValue(sessionForm.endTimeLocal);
        if (sessionForm.timeSpent) {
          payload.time_spent = Number.parseInt(sessionForm.timeSpent, 10);
        }
        if (checkOutIso) {
          payload.check_out_time = checkOutIso;
        }
      }
      const parsedSession = Number.parseInt(sessionForm.timeSpent, 10);
      const fallbackSegment = sessionForm.manual && Number.isFinite(parsedSession)
        ? Math.max(parsedSession - lunchMinutes, 0)
        : sessionNetMinutes(checkInTime, lunchMinutes);
      const response = await axios.post(`/api/tasks/${id}/pause-checkout`, payload);
      const apiSegment = Number(response.data?.data?.time_spent);
      const segmentMinutes = Number.isFinite(apiSegment) ? apiSegment : fallbackSegment;
      const priorMinutes = cumulativeWorkedMinutes(task?.check_ins, user.id);
      const visitTotal = priorMinutes + segmentMinutes;
      setSessionModalMode(null);
      setCheckInTime(null);
      const refreshed = await refreshTaskPreservingScroll();
      if (refreshed) {
        setSuccess(t('tasks:pauseCheckoutSuccess', {
          segment: formatTime(segmentMinutes),
          total: formatTime(visitTotal)
        }));
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedPauseCheckout'));
    } finally {
      setSessionSubmitting(false);
    }
  };

  const handleDoneCheckout = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('summary', checkOutData.summary);
    formData.append(
      'lunch_minutes',
      String(parsePauseLunchMinutes(sessionForm.took_lunch, sessionForm.lunch_minutes))
    );
    // Omit corrections when unchanged so sub-minute "now" checkout still works.
    if (sessionForm.manual) {
      formData.append('timeSpent', sessionForm.timeSpent);
      const checkOutIso = fromDateTimeLocalValue(sessionForm.endTimeLocal);
      if (checkOutIso) {
        formData.append('check_out_time', checkOutIso);
      }
    }

    checkOutData.beforePhotos.forEach((file) => {
      formData.append('beforePhotos', file);
    });

    checkOutData.afterPhotos.forEach((file) => {
      formData.append('afterPhotos', file);
    });

    const lines = await ensureTaskArticleLines();
    const qtyPayload = buildArticleQuantitiesPayload(lines, doneArticleQuantities);
    if (qtyPayload.error) {
      setError(t('tasks:taskArticlesQuantityInvalid'));
      return;
    }
    if (qtyPayload.payload.length > 0) {
      formData.append('articleQuantities', JSON.stringify(qtyPayload.payload));
    }

    try {
      setSessionSubmitting(true);
      setMarkDoneSubmitting(true);
      await axios.post(`/api/tasks/${id}/checkout`, formData);
      setSessionModalMode(null);
      setCheckOutData({
        summary: '',
        beforePhotos: [],
        afterPhotos: []
      });
      setCheckInTime(null);
      const refreshed = await refreshTaskPreservingScroll();
      if (refreshed) {
        setSuccess(t('tasks:checkOutDoneSuccess'));
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedCheckOut'));
    } finally {
      setSessionSubmitting(false);
      setMarkDoneSubmitting(false);
    }
  };

  const submitMarkDoneWithoutCheckIn = async (lines = taskArticleLines) => {
    const qtyPayload = buildArticleQuantitiesPayload(lines, doneArticleQuantities);
    if (qtyPayload.error) {
      setError(t('tasks:taskArticlesQuantityInvalid'));
      return;
    }
    try {
      setMarkDoneSubmitting(true);
      await axios.post(`/api/tasks/${id}/complete`, {
        articleQuantities: qtyPayload.payload
      });
      setMarkDoneArticlesModalOpen(false);
      const refreshed = await refreshTaskPreservingScroll();
      if (refreshed) {
        setSuccess(t('tasks:checkOutDoneSuccess'));
      }
    } catch (error) {
      setError(formatError(error) || t('tasks:failedMarkDoneNoTime'));
    } finally {
      setMarkDoneSubmitting(false);
    }
  };

  const handleMarkDoneWithoutCheckIn = async () => {
    setError('');
    setSuccess('');
    // No confirm dialog — go straight to articles step or complete.
    const lines = await ensureTaskArticleLines();
    const hasDoneQtyLines = lines.some(
      (line) => line.unitMode === 'quantity' || line.unitMode === 'both'
    );
    // Always open articles step when admin can add, or when qty lines need input.
    if (isCompanyAdmin(user) || hasDoneQtyLines) {
      setMarkDoneArticlesModalOpen(true);
      return;
    }

    await submitMarkDoneWithoutCheckIn(lines);
  };

  const handleFileChange = (e, type) => {
    const files = Array.from(e.target.files);
    setCheckOutData({
      ...checkOutData,
      [type]: files
    });
  };

  const handleEditBannerChange = (file) => {
    setEditBannerFile(file);
    if (file) {
      setRemoveTaskBanner(false);
    }
  };

  const handleRemoveTaskBanner = () => {
    setEditBannerFile(null);
    setRemoveTaskBanner(true);
    if (editBannerInputRef.current) {
      editBannerInputRef.current.value = '';
    }
  };

  const openRemoveModal = () => {
    setRemoveReason('');
    setRemoveModalOpen(true);
  };

  const handleRemoveTask = async (event) => {
    event.preventDefault();
    const reason = removeReason.trim();
    if (!reason) {
      setError(t('tasks:removeReasonRequired'));
      return;
    }
    setRemoveSubmitting(true);
    setError('');
    try {
      await axios.post(`/api/tasks/${id}/remove`, { reason });
      setRemoveModalOpen(false);
      setRemoveReason('');
      await fetchTask();
    } catch (err) {
      setError(formatError(err) || t('tasks:failedRemoveTask'));
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const handlePermanentlyDeleteTask = async () => {
    if (!(await confirm(t('tasks:confirmPermanentDelete'), { danger: true }))) {
      return;
    }
    setPermanentDeleteSubmitting(true);
    setError('');
    try {
      await axios.delete(`/api/tasks/${id}`);
      navigate('/tasks');
    } catch (err) {
      setError(formatError(err) || t('tasks:failedPermanentDelete'));
      setPermanentDeleteSubmitting(false);
    }
  };

  const handleRestoreTask = async () => {
    if (!(await confirm(t('tasks:confirmRestoreTask')))) {
      return;
    }
    setError('');
    try {
      await axios.post(`/api/tasks/${id}/restore`);
      await fetchTask();
    } catch (err) {
      setError(formatError(err) || t('tasks:failedRestoreTask'));
    }
  };

  const isAssigned = task?.assignments?.some((a) => a.assigned_to === user.id);
  const isCompleted = task?.status === 'completed';
  const isDraft = task?.status === 'draft';
  const isRemoved = isTaskRemoved(task);
  const isDraftCreator = isDraft && task?.created_by === user?.id;
  const canEditDraftAsEmployee = isDraftCreator && isCompanyEmployee(user);
  const canEdit = (isCompanyAdmin(user) || canEditDraftAsEmployee) && !isRemoved;
  const canAdminDraft = isDraft && isCompanyAdmin(user);
  const canRemove = isCompanyAdmin(user) && !isDraft && !isRemoved;
  const canRestore = isCompanyAdmin(user) && isRemoved;
  const canPermanentlyDelete =
    (isCompanyAdmin(user) && (isRemoved || isDraft))
    || canEditDraftAsEmployee;
  const canCheckIn =
    !isDraft &&
    !isRemoved &&
    canParticipateInAssignedTasks(user) &&
    isAssigned &&
    !checkInTime &&
    !isCompleted;
  const canCheckOut =
    !isDraft &&
    !isRemoved &&
    canParticipateInAssignedTasks(user) &&
    isAssigned &&
    !!checkInTime &&
    !isCompleted;
  const canMarkDoneWithoutCheckIn = canCheckIn;
  const canMutateArticles = isCompanyAdmin(user)
    || (!!checkInTime && !isCompleted && isAssigned);
  const renderDoneArticlesStep = () => (
    <div className="task-done-articles-dialog">
      <p className="section-description">{t('tasks:taskArticlesDoneQuantitiesHelp')}</p>
      <TaskArticlesSection
        taskId={id}
        companyId={task.company_id}
        canMutate={canMutateArticles}
        isCompleted={isCompleted}
        compact
        seedLines={taskArticleLines}
        seedArticles={articles}
        onLinesChange={handleTaskArticleLinesChange}
        quantities={doneArticleQuantities}
        onQuantityChange={(lineId, value) => setDoneArticleQuantities((prev) => ({
          ...prev,
          [lineId]: value
        }))}
      />
    </div>
  );
  const canAssign = isCompanyAdmin(user) && !isCompleted && !isDraft && !isRemoved;
  const claimable = canClaimTask(user, task);
  const canUploadTaskImages = canAccessFeature(user, 'taskImageUploads');
  const bannerUrl = task?.effective_banner_image_url || task?.banner_image_url || task?.location_banner_image_url;
  const hasCustomTaskBanner = Boolean(task?.banner_image_url);
  const editBannerExistingUrl = removeTaskBanner
    ? task?.location_banner_image_url
    : (task?.banner_image_url || task?.location_banner_image_url);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!task) {
    return (
      <div className="container container--wide main-content">
        <div className="error">{t('tasks:taskNotFound')}</div>
      </div>
    );
  }

  const hasPhotoContent = Array.isArray(task.check_ins) && task.check_ins.some(checkInHasPhotos);
  const showHistoryCard = Array.isArray(task.check_ins) && task.check_ins.length > 0;
  const showActionsCard = canCheckIn || canCheckOut || canMarkDoneWithoutCheckIn;
  // Keep session slots mounted for assigned workers so check-in does not reshuffle columns.
  const showWorkSessionCards =
    !isDraft &&
    !isCompleted &&
    isAssigned &&
    canParticipateInAssignedTasks(user);
  const showPhotosCard = showWorkSessionCards || hasPhotoContent;

  const freeCards = [];

  if (task.description) {
    freeCards.push({
      key: 'description',
      node: (
        <div className="task-detail-section card">
          <h3 className="task-section-label">{t('tasks:descriptionLabel')}</h3>
          <p className="task-description-text">{task.description}</p>
        </div>
      )
    });
  }

  if (!isDraft) {
    const unitModeLabel = (mode) => {
      if (mode === 'quantity') return t('articles:unitModeQuantity');
      if (mode === 'time') return t('articles:unitModeTime');
      if (mode === 'both') return t('articles:unitModeBoth');
      return mode || '';
    };
    freeCards.push({
      key: 'article',
      node: (
        <div className="task-detail-section card">
          <h3 className="task-section-label">{t('tasks:articleSection')}</h3>
          {task.article_id && task.article_name ? (
            <div className="task-article-detail">
              <p className="task-article-name">{task.article_name}</p>
              {task.article_unit_mode && (
                <p className="task-article-meta">
                  {t('tasks:articleUnitMode', { mode: unitModeLabel(task.article_unit_mode) })}
                </p>
              )}
              {(task.article_unit_mode === 'quantity' || task.article_unit_mode === 'both') &&
                task.article_quantity != null && (
                  <p className="task-article-meta">
                    {t('tasks:articleQuantityLabel')}: {task.article_quantity}
                  </p>
                )}
              {(task.article_unit_mode === 'time' || task.article_unit_mode === 'both') &&
                task.estimated_duration != null && (
                  <p className="task-article-meta">
                    {t('tasks:estimatedDurationLabel')}: {task.estimated_duration}
                  </p>
                )}
            </div>
          ) : (
            <p className="task-article-missing">{t('tasks:articleOptionalEmpty')}</p>
          )}
        </div>
      )
    });
  }

  freeCards.push({
    key: 'assignment',
    node: (
      <div className="task-detail-section card">
        <h3 className="task-section-label">{t('tasks:assignmentSection')}</h3>
        {task.assignments && task.assignments.length > 0 ? (
          <div className="task-assignment-list">
            {task.assignments.map((assignment) => (
              <div key={assignment.id} className="task-assignment-item">
                <UserAvatar
                  user={{
                    first_name: assignment.first_name,
                    last_name: assignment.last_name,
                    profile_image_url: assignment.profile_image_url
                  }}
                  size={40}
                />
                <span className="task-assignment-name">
                  {assignment.assigned_to_name || `${assignment.first_name} ${assignment.last_name}`}
                </span>
                {canAssign && task.status !== 'completed' && (
                  <button
                    className="btn btn-danger btn-sm task-unassign-button"
                    onClick={() => handleUnassign(assignment.id)}
                  >
                    {t('tasks:unassign')}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="task-assignment-empty">
            {claimable ? t('tasks:unassignedClaimHint') : t('tasks:unassignedEmpty')}
          </p>
        )}
        {canAssign && !isCompleted && (
          <div className="form-group task-add-assignee">
            <label>{t('tasks:addAssignee')}</label>
            <UserAssignPicker
              users={users}
              multi={false}
              excludeIds={(task.assignments || []).map((assignment) => assignment.assigned_to)}
              onSelect={handleAssignUser}
              placeholder={t('tasks:placeholderAssignUser')}
            />
          </div>
        )}
      </div>
    )
  });

  if (!isDraft) {
    freeCards.push({
      key: 'articles',
      node: (
        <div className="card task-detail-nested-card">
          <TaskArticlesSection
            taskId={id}
            companyId={task.company_id}
            canMutate={canMutateArticles}
            isCompleted={isCompleted}
            onLinesChange={handleTaskArticleLinesChange}
            syncedLines={taskArticleLines}
          />
        </div>
      )
    });
  }

  if (isCompanyAdmin(user) && !isDraft) {
    freeCards.push({
      key: 'billing-status',
      node: (
        <div className="task-detail-section card">
          <TaskBillingStatusSection
            task={task}
            onUpdated={(billing) => {
              setTask((prev) => (prev ? { ...prev, ...billing } : prev));
            }}
          />
        </div>
      )
    });
    freeCards.push({
      key: 'fortnox',
      node: (
        <div className="card task-detail-nested-card">
          <TaskFortnoxBillableSection taskId={id} disabled={!canEdit && isCompleted} />
        </div>
      )
    });
  }

  if (task.location_id && locationHandouts.length > 0) {
    freeCards.push({
      key: 'handouts',
      node: (
        <div className="task-detail-section card">
          <h3 className="task-section-label">{t('tasks:locationHandoutsLabel')}</h3>
          <div className="task-handout-list">
            {locationHandouts.map((handout) => (
              <div key={handout.id} className="task-handout-item">
                <div className="task-handout-info">
                  <span className="task-handout-name">{handout.name}</span>
                  {handout.description && (
                    <span className="task-handout-description">{handout.description}</span>
                  )}
                </div>
                {renderHandoutStatus(handout)}
              </div>
            ))}
          </div>
        </div>
      )
    });
  }

  if (task.checklist && task.checklist.length > 0) {
    freeCards.push({
      key: 'checklist',
      node: (
        <div className="card task-detail-nested-card">
          <TaskChecklist
            checklist={task.checklist}
            taskId={id}
            canCheckItems={isAssigned && !isCompleted && (!!checkInTime || task.status === 'in_progress')}
            taskStatus={task.status}
            onUpdated={fetchTask}
            onError={setError}
          />
        </div>
      )
    });
  }

  if (showWorkSessionCards) {
    freeCards.push({
      key: 'notes',
      node: canCheckOut ? (
        <TaskNotesCard
          checkOutData={checkOutData}
          setCheckOutData={setCheckOutData}
        />
      ) : (
        renderIdleSessionCard('tasks:notesLabel', 'tasks:checkInToContinueHint')
      )
    });
    freeCards.push({
      key: 'time',
      node: canCheckOut ? (
        <TaskTimeSummaryCard
          checkInTime={checkInTime}
          checkIns={task.check_ins}
          userId={user.id}
          formatTime={formatTime}
        />
      ) : (
        renderIdleSessionCard('tasks:timeSection', 'tasks:checkInToLogTimeHint')
      )
    });
  }

  if (showPhotosCard) {
    freeCards.push({
      key: 'photos',
      node: canCheckOut || hasPhotoContent ? (
        <TaskPhotos
          showUpload
          canCheckOut={canCheckOut}
          canUploadTaskImages={canUploadTaskImages}
          checkOutData={checkOutData}
          onFileChange={handleFileChange}
          checkIns={task.check_ins}
          onSelectPhoto={setSelectedPhoto}
        />
      ) : (
        renderIdleSessionCard('tasks:photosSection', 'tasks:checkInToAddPhotosHint')
      )
    });
  }

  if (isCompleted) {
    freeCards.push({
      key: 'completed',
      node: <TaskCompletedCard />
    });
  }

  const stickyPair = [];
  if (showActionsCard) {
    stickyPair.push({
      key: 'actions',
      node: (
        <TaskActionsCard
          canCheckIn={canCheckIn}
          canCheckOut={canCheckOut}
          canMarkDoneWithoutCheckIn={canMarkDoneWithoutCheckIn}
          onCheckIn={handleCheckIn}
          onMarkDoneWithoutCheckIn={handleMarkDoneWithoutCheckIn}
          onOpenPauseModal={() => openSessionModal('pause')}
          onOpenDoneModal={() => openSessionModal('done')}
          markDoneSubmitting={markDoneSubmitting}
        />
      )
    });
  }
  if (showHistoryCard) {
    stickyPair.push({
      key: 'history',
      node: <TaskCheckInHistory checkIns={task.check_ins} formatTime={formatTime} />
    });
  }

  const { left: leftCards, right: rightCards } = balanceTaskDetailColumns(freeCards, stickyPair);

  return (
    <div className="task-detail">
      <div className="container container--wide main-content">
        <TaskDetailHeader
          task={task}
          user={user}
          isDraft={isDraft}
          canEdit={canEdit}
          canEditDraftAsEmployee={canEditDraftAsEmployee}
          canAdminDraft={canAdminDraft}
          editing={editing}
          bannerUrl={bannerUrl}
          canClaim={claimable && !isRemoved}
          claiming={claiming}
          onClaim={handleClaimTask}
          onEdit={handleEdit}
          canRemove={canRemove}
          canRestore={canRestore}
          canPermanentlyDelete={canPermanentlyDelete}
          permanentDeleteSubmitting={permanentDeleteSubmitting}
          onRemove={openRemoveModal}
          onRestore={handleRestoreTask}
          onPermanentlyDelete={handlePermanentlyDeleteTask}
        />

        {error && <div className="error">{error}</div>}
        {success && (
          <div className="success task-detail-status" role="status">
            {success}
          </div>
        )}

        <div className="task-detail-body-grid">
          <div className="task-detail-col">
            {leftCards.map((card) => (
              <React.Fragment key={card.key}>{card.node}</React.Fragment>
            ))}
          </div>
          <div className="task-detail-col">
            {rightCards.map((card) => (
              <React.Fragment key={card.key}>{card.node}</React.Fragment>
            ))}
          </div>
        </div>

        <TaskSessionEndModal
          mode={sessionModalMode || 'pause'}
          isOpen={!!sessionModalMode}
          onClose={() => !sessionSubmitting && setSessionModalMode(null)}
          form={sessionForm}
          setForm={setSessionForm}
          submitting={sessionSubmitting}
          onSubmit={sessionModalMode === 'done' ? handleDoneCheckout : handlePauseCheckout}
          checkInTime={checkInTime}
          checkIns={task.check_ins}
          userId={user.id}
          formatTime={formatTime}
          formatDateTime={formatDateTime}
          extraContent={renderDoneArticlesStep()}
        />

        <Modal
          isOpen={markDoneArticlesModalOpen}
          onClose={() => !markDoneSubmitting && setMarkDoneArticlesModalOpen(false)}
          title={t('tasks:taskArticlesDoneQuantitiesTitle')}
        >
          {renderDoneArticlesStep()}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={markDoneSubmitting}
              onClick={() => submitMarkDoneWithoutCheckIn()}
            >
              {markDoneSubmitting ? t('tasks:markingTaskDone') : t('tasks:markTaskDone')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={markDoneSubmitting}
              onClick={() => setMarkDoneArticlesModalOpen(false)}
            >
              {t('common:cancel')}
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={removeModalOpen}
          onClose={() => !removeSubmitting && setRemoveModalOpen(false)}
          title={t('tasks:removeTaskTitle')}
        >
          <form className="task-remove-form" onSubmit={handleRemoveTask}>
            <p className="task-remove-help">{t('tasks:removeTaskHelp')}</p>
            <div className="form-group">
              <label htmlFor="remove-reason">{t('tasks:removeReasonLabel')}</label>
              <textarea
                id="remove-reason"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder={t('tasks:removeReasonPlaceholder')}
                rows={4}
                required
                disabled={removeSubmitting}
              />
            </div>
            <div className="task-remove-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRemoveModalOpen(false)}
                disabled={removeSubmitting}
              >
                {t('common:cancel')}
              </button>
              <button type="submit" className="btn btn-danger" disabled={removeSubmitting || !removeReason.trim()}>
                {removeSubmitting ? t('tasks:removingTask') : t('tasks:confirmRemoveTask')}
              </button>
            </div>
          </form>
        </Modal>

        <p className="task-detail-meta-footer">
          {t('tasks:taskMetaCreated', {
            createdAt: formatDateTime(task.created_at),
            creator: task.created_by_name || t('tasks:unknownSubmitter')
          })}
        </p>

        {/* Edit Task Modal */}
        <Modal
          isOpen={editing}
          onClose={handleEditCancel}
          title={canAdminDraft ? t('tasks:reviewPublishDraftTitle') : t('tasks:editTaskTitle')}
          className="task-detail-edit-modal"
        >
          <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>{t('tasks:titleLabel')}</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('tasks:descriptionLabel')}</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows="4"
                />
              </div>
              {!canEditDraftAsEmployee && (
              <>
              <div className="form-row">
                <div className="form-group">
                  <label>{t('tasks:priorityLabel')}</label>
                  <select
                    value={editFormData.priority}
                    onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                  >
                    <option value="low">{formatTaskPriority('low', t)}</option>
                    <option value="medium">{formatTaskPriority('medium', t)}</option>
                    <option value="high">{formatTaskPriority('high', t)}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('tasks:estimatedDurationLabel')}</label>
                  <input
                    type="number"
                    value={editFormData.estimatedDuration}
                    onChange={(e) => setEditFormData({ ...editFormData, estimatedDuration: e.target.value })}
                    min="0"
                  />
                </div>
              </div>
              <TaskArticleFields
                articles={articles}
                formData={editFormData}
                onChange={handleArticleFormChange}
              />
              <div className="form-row">
                <div className="form-group">
                  <label>{t('tasks:scheduledDateLabel')}</label>
                  <input
                    type="date"
                    value={editFormData.scheduledFor}
                    onChange={(e) => setEditFormData({ ...editFormData, scheduledFor: e.target.value })}
                  />
                  <small className="form-hint">
                    {t('tasks:scheduledDateRemoveHint')}
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('tasks:locationLabel')}</label>
                  <select
                    value={editFormData.locationId || ''}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        locationId: e.target.value
                      })
                    }
                  >
                    <option value="">{t('tasks:noSpecificLocation')}</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  {editFormData.locationId && (() => {
                    const selectedLoc = locations.find(
                      (l) => String(l.id) === String(editFormData.locationId)
                    );
                    if (!selectedLoc) {
                      return null;
                    }
                    const hasImage = Boolean(selectedLoc.banner_image_url);
                    const hasAddress = Boolean(selectedLoc.address);
                    if (!hasImage && !hasAddress) {
                      return null;
                    }
                    return (
                      <div className="task-location-preview">
                        {hasImage && (
                          <img
                            src={getUploadUrl(selectedLoc.banner_image_url)}
                            alt={selectedLoc.name}
                          />
                        )}
                        {hasAddress && (
                          <small className="task-location-preview-address">
                            {selectedLoc.address}
                          </small>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="form-group">
                <label>{t('tasks:customerLabel')}</label>
                <select
                  value={editFormData.customerId || ''}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      customerId: e.target.value
                    })
                  }
                >
                  <option value="">{t('tasks:noCustomer')}</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                      {customer.organization_name ? ` (${customer.organization_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {isCompanyAdmin(user) && canAccessFeature(user, 'taskWorkGroups') && (
                <div className="form-group">
                  <label>{t('tasks:workGroupLabel')}</label>
                  <WorkGroupSelect
                    workGroups={workGroups}
                    workGroupId={editFormData.workGroupId || ''}
                    workGroupName={editFormData.workGroupName || ''}
                    onChange={({ workGroupId, workGroupName }) => {
                      setEditFormData((prev) => ({
                        ...prev,
                        workGroupId: workGroupId || '',
                        workGroupName: workGroupName || ''
                      }));
                    }}
                  />
                </div>
              )}
              <div className="form-group">
                <div className="form-recurrence-row">
                  <label>{t('tasks:recurrenceToggleLabel')}</label>
                  <div 
                    className={`task-toggle-switch ${editFormData.recurrenceEnabled ? 'active' : ''}`}
                    onClick={() =>
                      setEditFormData({
                        ...editFormData,
                        recurrenceEnabled: !editFormData.recurrenceEnabled
                      })
                    }
                  >
                    <div className="task-toggle-knob"></div>
                  </div>
                </div>
              </div>
              {editFormData.recurrenceEnabled && (
                <div className="form-group">
                  <label>{t('tasks:recurrenceWeekdaysLabel')}</label>
                  <RecurrenceWeekdayPicker
                    idPrefix="edit-recurrence-day"
                    value={editFormData.recurrenceWeekdays || []}
                    onChange={(recurrenceWeekdays) =>
                      setEditFormData({
                        ...editFormData,
                        recurrenceWeekdays,
                        recurrenceIntervalDays: ''
                      })
                    }
                  />
                  <small className="recurrence-weekday-hint">{t('tasks:recurrenceWeekdaysHint')}</small>
                </div>
              )}
              <div className="form-group">
                <label>{t('tasks:checklistLabel')}</label>
                {presetChecklists.length > 0 && (
                <div className="form-preset-block">
                  <label className="form-preset-label">{t('tasks:usePresetChecklistLabel')}</label>
                  <select
                    className="form-select-full"
                    value={(() => {
                      const matchingPreset = findMatchingChecklistPreset();
                      return matchingPreset ? matchingPreset.id : '';
                    })()}
                    onChange={handlePresetChecklistChange}
                  >
                    <option value="">{t('tasks:selectPresetChecklist')}</option>
                    {presetChecklists.map((preset) => (
                      <option key={preset.id} value={preset.id}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                </div>
                )}
                <div className="checklist-editor">
                  {Array.isArray(editFormData.checklist) && editFormData.checklist.map((item, index) => {
                    const itemText = typeof item === 'string' ? item : (item.text || item);
                    return (
                      <div key={index} className="checklist-item">
                        <span className="checklist-item-name">{itemText}</span>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveChecklistItem(index)}
                        >
                          {t('tasks:removeChecklistItem')}
                        </button>
                      </div>
                    );
                  })}
                  <div className="add-checklist-item">
                    <input
                      type="text"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder={t('tasks:placeholderChecklistItem')}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddChecklistItem();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleAddChecklistItem}
                    >
                      {t('tasks:addChecklistItem')}
                    </button>
                  </div>
                </div>
              </div>
              {canAdminDraft && (
                <div className="form-group">
                  <label>{t('tasks:assignOnPublishLabel')}</label>
                  <UserAssignPicker
                    users={users}
                    multi
                    selectedUsers={publishAssignees}
                    onChange={setPublishAssignees}
                    placeholder={t('tasks:placeholderPublishAssign')}
                  />
                </div>
              )}
              </>
              )}
              <div className="form-group">
                <label>{t('tasks:bannerImageLabel')}</label>
                {canUploadTaskImages ? (
                  <>
                    <BannerImageField
                      existingUrl={editBannerExistingUrl}
                      selectedFile={editBannerFile}
                      onFileSelect={handleEditBannerChange}
                      onRemove={
                        (hasCustomTaskBanner && !removeTaskBanner) || editBannerFile
                          ? handleRemoveTaskBanner
                          : null
                      }
                      removeLabel={
                        hasCustomTaskBanner
                          ? t('tasks:useLocationBannerLabel')
                          : t('tasks:removeBannerLabel')
                      }
                      inputRef={editBannerInputRef}
                    />
                    {task?.location_banner_image_url && !hasCustomTaskBanner && !editBannerFile && !canEditDraftAsEmployee && (
                      <small>{t('tasks:locationBannerOverrideHint')}</small>
                    )}
                  </>
                ) : task?.location_banner_image_url || task?.effective_banner_image_url ? (
                  <small>{t('tasks:locationBannerUpgradeHint')}</small>
                ) : (
                  <UpgradeCta as="small" />
                )}
              </div>
                {error && <div className="error">{error}</div>}
                <div className="modal-actions">
                  {canAdminDraft ? (
                    <>
                      <button type="button" className="btn btn-primary" onClick={handlePublish}>
                        {t('tasks:publish')}
                      </button>
                      <button type="submit" className="btn btn-secondary">
                        {t('tasks:saveDraft')}
                      </button>
                    </>
                  ) : (
                    <button type="submit" className="btn btn-primary">
                      {t('tasks:saveChanges')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleEditCancel}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
      <Lightbox isOpen={!!selectedPhoto} onClose={() => setSelectedPhoto(null)}>
        <img
          className="lightbox-image"
          src={selectedPhoto?.file_url}
          alt={
            selectedPhoto?.photo_type === 'before'
              ? t('tasks:beforePhotoAlt')
              : t('tasks:afterPhotoAlt')
          }
        />
        <div className="lightbox-meta">
          <p>
            <strong>
              {selectedPhoto?.photo_type === 'before'
                ? t('tasks:beforePhotoCaption')
                : t('tasks:afterPhotoCaption')}
            </strong>
          </p>
          {selectedPhoto?.user_name && (
            <p>{t('tasks:photoBy', { name: selectedPhoto.user_name })}</p>
          )}
          {selectedPhoto?.check_in_time && (
            <p>
              {t('tasks:photoCheckIn', {
                datetime: formatDateTime(selectedPhoto.check_in_time)
              })}
            </p>
          )}
          {selectedPhoto?.uploaded_at && (
            <p>
              {t('tasks:photoUploaded', {
                datetime: formatDateTime(selectedPhoto.uploaded_at)
              })}
            </p>
          )}
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setSelectedPhoto(null)}>
          {t('common:close')}
        </button>
      </Lightbox>
    </div>
  );
};

export default TaskDetail;
